import type { Crop, PixelCrop } from 'react-image-crop';
import { centerCrop, makeAspectCrop } from 'react-image-crop';

const DEFAULT_MAX_OUTPUT_WIDTH = 1920;

export function resolveCropOutputMime(sourceMime: string): string {
  if (sourceMime === 'image/jpeg' || sourceMime === 'image/jpg') {
    return 'image/jpeg';
  }
  if (sourceMime === 'image/webp') {
    return 'image/webp';
  }
  if (sourceMime === 'image/png' || sourceMime === 'image/gif') {
    return 'image/png';
  }
  return 'image/jpeg';
}

export function resolveCropFileName(originalName: string, mime: string): string {
  const base = originalName.replace(/\.[^.]+$/, '') || 'image';
  const ext = mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : 'jpg';
  return `${base}-cropped.${ext}`;
}

export async function cropImageToFile(
  image: HTMLImageElement,
  crop: PixelCrop,
  originalFile: File,
  options?: { maxOutputWidth?: number },
): Promise<File> {
  const mime = resolveCropOutputMime(originalFile.type);
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  let outWidth = Math.max(1, Math.round(crop.width * scaleX));
  let outHeight = Math.max(1, Math.round(crop.height * scaleY));
  const maxWidth = options?.maxOutputWidth ?? DEFAULT_MAX_OUTPUT_WIDTH;
  if (outWidth > maxWidth) {
    const ratio = maxWidth / outWidth;
    outWidth = maxWidth;
    outHeight = Math.max(1, Math.round(outHeight * ratio));
  }

  canvas.width = outWidth;
  canvas.height = outHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas is not supported');
  }

  context.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    outWidth,
    outHeight,
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          resolve(result);
          return;
        }
        reject(new Error('Failed to crop image'));
      },
      mime,
      mime === 'image/jpeg' ? 0.92 : undefined,
    );
  });

  return new File([blob], resolveCropFileName(originalFile.name, mime), { type: mime });
}

export function buildInitialCrop(width: number, height: number, aspect: number): Crop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      width,
      height,
    ),
    width,
    height,
  );
}
